import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostBinding, Injector, Input, forwardRef, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideNativeDateAdapter, MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { DateRange, DefaultMatCalendarRangeStrategy, MatCalendar, MatDatepickerModule, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import moment from 'moment';
import { MatIconModule } from '@angular/material/icon';
import { CapitalizePipe } from '@app/shared/pipes/capitalize.pipe';
import { DatePipe, NgClass, registerLocaleData } from '@angular/common';
import { REX_DATE_FORMAT, RexCustomDateAdapter } from '@app/models/rex-custom-date-adapter.model';
import localeEs from '@angular/common/locales/es';

registerLocaleData(localeEs);

let nextUniqueId = 0;

@Component({
  selector: 'app-rex-date-range',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgClass,
    MatSelectModule,
    MatListModule,
    MatExpansionModule,
    MatDatepickerModule,
    MatCalendar,
    MatIconModule,
    CapitalizePipe
  ],
  providers: [
    DefaultMatCalendarRangeStrategy,
    MatRangeDateSelectionModel,
    provideNativeDateAdapter(),
    { provide: MatFormFieldControl, useExisting: forwardRef(() => RexDateRangeComponent) },
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => RexDateRangeComponent),
    },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    { provide: MAT_DATE_FORMATS, useValue: REX_DATE_FORMAT },
    { provide: DateAdapter, useClass: RexCustomDateAdapter, deps: [MAT_DATE_FORMATS, MAT_DATE_LOCALE] },
  ],
  templateUrl: './rex-date-range.component.html',
  styleUrl: './rex-date-range.component.scss'
})
export class RexDateRangeComponent implements MatFormFieldControl<DateRange<Date | null>>, ControlValueAccessor {

  // MatFormFieldControl @Input() getter/setters — must remain @Input() for interface compliance
  @Input() touched = false;

  @Input()
  set required(value: boolean) { this._required = value; this.stateChanges.next(); }
  get required(): boolean { return this._required; }
  private _required = false;

  @Input()
  set placeholder(value: string) { this._placeholder = value; this.stateChanges.next(); }
  get placeholder(): string { return this._placeholder; }
  private _placeholder = '';

  @Input()
  set disabled(value: boolean) { this._disabled = value; this.stateChanges.next(); }
  get disabled(): boolean { return this._disabled; }
  private _disabled = false;

  @Input()
  get value(): DateRange<Date | null> | null { return this._value; }
  set value(dateRange: DateRange<Date | null> | null) { this._value = dateRange; this.stateChanges.next(); }
  private _value: DateRange<Date | null> | null = null;

  // DI
  private readonly selectionModel = inject(MatRangeDateSelectionModel<Date>);
  private readonly selectionStrategy = inject(DefaultMatCalendarRangeStrategy<Date>);
  private readonly injector = inject(Injector);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  // ViewChildren
  readonly calendarLeft = viewChild<MatCalendar<Date>>('calendarLeft');
  readonly calendarRigth = viewChild<MatCalendar<Date>>('calendarRigth');
  readonly matSelect = viewChild(MatSelect);

  // MatFormFieldControl required properties
  readonly stateChanges = new Subject<void>();
  readonly id = `rex-date-range-${nextUniqueId++}`;
  ngControl: NgControl | null = null;
  focused = false;
  errorState = false;
  controlType = 'rex-date-range';
  autofilled?: boolean;
  userAriaDescribedBy?: string;

  // Component state
  selected: DateRange<Date | null> | undefined;
  dateRangeOptions: { description: string; function: () => void }[] = [];
  showCustomMonth = false;
  panelClass = 'rex-date-range-fit';
  absoluteMaxDate: Date;
  maxDate: Date | undefined;
  startDateLeft: Date | null = null;
  startDateRigth: Date | null = null;

  // ControlValueAccessor callbacks
  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  @HostBinding('class.floating')
  get shouldLabelFloat() { return this.focused || !this.empty; }

  @HostBinding('id')
  get hostId() { return this.id; }

  @HostBinding('attr.aria-describedby')
  get describedBy() { return this.userAriaDescribedBy; }

  get empty() { return !this._value || (!this._value.start && !this._value.end); }

  constructor() {
    this.absoluteMaxDate = moment().endOf('day').toDate();
    this.maxDate = this.absoluteMaxDate;

    try {
      this.ngControl = this.injector.get(NgControl, null);
      if (this.ngControl != null) {
        this.ngControl.valueAccessor = this;
      }
    } catch { }

    this.dateRangeOptions = [
      { description: 'Últimas 24 horas', function: () => this.setValueControl('last24hours') },
      { description: 'Ayer', function: () => this.setValueControl('yesterday') },
      { description: 'Última semana', function: () => this.setValueControl('lastWeek') },
      { description: 'Último mes', function: () => this.setValueControl('lastMonth') },
      { description: 'Últimos 3 meses', function: () => this.setValueControl('last3Month') },
      { description: 'Seleccionar', function: () => this.openSectionCustomMonth() },
    ];

    this.focusMonitor.monitor(this.elRef.nativeElement, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(origin => {
        this.focused = !!origin;
        this.stateChanges.next();
      });

    this.destroyRef.onDestroy(() => {
      this.stateChanges.complete();
      this.focusMonitor.stopMonitoring(this.elRef.nativeElement);
    });
  }

  setValueControl(typeData: string, param?: any) {
    const mapFunction: { [key: string]: () => any } = {
      'last24hours': () => this.getDateRangeLast24hours(),
      'yesterday': () => this.getDateRangeYesterday(),
      'lastWeek': () => this.getDateRangeLastWeek(),
      'lastMonth': () => this.getDateRangeLastMonth(),
      'last3Month': () => this.getDateRangeLast3Month(),
      'customDate': () => this.getDateRangeCustom(param!),
    };
    const result = mapFunction[typeData] ? mapFunction[typeData]() : '';

    if (result !== null && result.start !== null && result.end !== null) {
      this.selected = new DateRange<Date>(result.start, result.end);
      this.sincronizeCalendar(result, typeData);
      this.value = result;
      this.onChange(this.value);
      this.onTouched();
      this.stateChanges.next();
      this.panelClass = 'rex-date-range-fit';
      this.matSelect()?.close();
    }
    if (typeData !== 'customDate') {
      this.showCustomMonth = false;
    }
  }

  sincronizeCalendar(result: DateRange<Date | null>, typeData?: string) {
    const mesMax = this.maxDate ? this.maxDate.getMonth() + 1 : 0;
    let mesRango = 0;

    if (result.start?.getMonth() === result.end?.getMonth()) {
      mesRango = result.start != null ? result.start.getMonth() + 1 : 0;
    }

    if (mesMax === mesRango) {
      this.startDateLeft = result.start
        ? new Date(result.start.getFullYear(), result.start.getMonth() - 1, 1)
        : null;
      this.startDateRigth = result.start ?? null;
    } else {
      this.startDateLeft = result.start ?? null;
      this.startDateRigth = result.start
        ? new Date(result.start.getFullYear(), result.start.getMonth() + 1, 1)
        : null;
    }
  }

  get displayValue(): string {
    if (!this.value || !this.value.start || !this.value.end) return '';
    const from = moment(this.value.start).format('DD/MM/YYYY');
    const to = moment(this.value.end).format('DD/MM/YYYY');
    return `${from} - ${to}`;
  }

  getDateRangeYesterday(): DateRange<Date> {
    const from = moment().subtract(1, 'day').startOf('day').toDate();
    const to = moment().subtract(1, 'day').endOf('day').toDate();
    return new DateRange<Date>(from, to);
  }

  getDateRangeLastWeek(): DateRange<Date> {
    const from = moment().subtract(1, 'week').startOf('day').toDate();
    const to = moment().endOf('day').toDate();
    return new DateRange<Date>(from, to);
  }

  getDateRangeLastMonth(): DateRange<Date> {
    const from = moment().subtract(1, 'month').startOf('day').toDate();
    const to = moment().endOf('day').toDate();
    return new DateRange<Date>(from, to);
  }

  getDateRangeLast3Month(): DateRange<Date> {
    const from = moment().subtract(3, 'month').startOf('day').toDate();
    const to = moment().endOf('day').toDate();
    return new DateRange<Date>(from, to);
  }

  getDateRangeLast24hours(): DateRange<Date> {
    const to = moment().toDate();
    const from = moment().subtract(24, 'hours').toDate();
    return new DateRange<Date>(from, to);
  }

  getDateRangeCustom(range: DateRange<Date>): DateRange<Date> {
    const from = range.start ? moment(range.start).startOf('day').toDate() : null;
    const to = range.end ? moment(range.end).endOf('day').toDate() : null;

    if (from && to) {
      const maxAllowedEnd = moment(from).add(3, 'months').endOf('day').toDate();
      const finalEnd = to > maxAllowedEnd ? maxAllowedEnd : to;
      const today = this.absoluteMaxDate;
      const validEnd = finalEnd > today ? today : finalEnd;
      return new DateRange<Date>(from, validEnd);
    }

    return new DateRange<Date>(from, to);
  }

  onDateSelected(selectedDate: Date | null) {
    if (!selectedDate) return;

    const selection = this.selectionModel.selection;
    const newSelection = this.selectionStrategy.selectionFinished(selectedDate, selection);

    if (newSelection.start && !newSelection.end) {
      const maxDateFromStart = moment(newSelection.start).add(3, 'months').endOf('day').toDate();
      this.maxDate = maxDateFromStart < this.absoluteMaxDate ? maxDateFromStart : this.absoluteMaxDate;
    } else if (newSelection.start && newSelection.end) {
      this.maxDate = this.absoluteMaxDate;
    }

    this.selectionModel.updateSelection(newSelection, this);
    this.selected = new DateRange<Date>(newSelection.start, newSelection.end);
    this.setValueControl('customDate', this.selected);
  }

  openSectionCustomMonth() {
    this.showCustomMonth = !this.showCustomMonth;
    if (this.showCustomMonth) {
      this.detectPanelPosition();
    } else {
      this.panelClass = 'rex-date-range-fit';
    }
  }

  handleSelection(selection: any) {
    this.touched = true;
    this.onTouched();
  }

  previousClicked() {
    if (this.startDateLeft) {
      this.startDateRigth = new Date(this.startDateLeft.getFullYear(), this.startDateLeft.getMonth(), 1);
      this.startDateLeft = new Date(this.startDateLeft.getFullYear(), this.startDateLeft.getMonth() - 1, 1);
      const left = this.calendarLeft();
      const right = this.calendarRigth();
      if (left) left.activeDate = this.startDateLeft;
      if (right) right.activeDate = this.startDateRigth;
    }
  }

  nextClicked() {
    if (this.startDateRigth) {
      const newStartDateLeft = new Date(this.startDateRigth.getFullYear(), this.startDateRigth.getMonth(), 1);
      const newStartDateRigth = new Date(this.startDateRigth.getFullYear(), this.startDateRigth.getMonth() + 1, 1);

      if (this.absoluteMaxDate && newStartDateRigth > this.absoluteMaxDate) return;

      this.startDateLeft = newStartDateLeft;
      this.startDateRigth = newStartDateRigth;
      const left = this.calendarLeft();
      const right = this.calendarRigth();
      if (left) left.activeDate = this.startDateLeft;
      if (right) right.activeDate = this.startDateRigth;
    }
  }

  // MatFormFieldControl methods
  setDescribedByIds(ids: string[]): void {
    this.userAriaDescribedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    const select = this.matSelect();
    if (select && !this.disabled) {
      select.focus();
      select.open();
    }
  }

  // ControlValueAccessor methods
  writeValue(obj: any): void {
    if (obj !== undefined) {
      this.value = obj;
      if (this.value && this.value.start && this.value.end) {
        this.selected = new DateRange<Date>(this.value.start, this.value.end);
        this.maxDate = this.value.end;
        this.startDateLeft = this.value.start;
        this.startDateRigth = new Date(this.value.start.getFullYear(), this.value.start.getMonth() + 1, 1);
      }
    }
  }

  registerOnChange(fn: any): void { this.onChange = fn; }

  registerOnTouched(fn: any): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  private detectPanelPosition(): void {
    if (this.elRef?.nativeElement) {
      const rect = this.elRef.nativeElement.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      this.panelClass = rect.right > windowWidth - 350
        ? 'rex-date-range-fit rex-date-range-left'
        : 'rex-date-range-fit';
    }
  }
}
